/**
 * Authentication API endpoints
 */

import apiClient from './client';
import { browserSessionClient, unwrapBrowserSession } from './browserSessionClient';
import { withBrowserSessionLock } from './browserSessionLock';
import { announceBrowserSessionMutation, clearCachedSessionData } from './authSession';
import { useAuthStore } from '@/stores/authStore';
import type { ApiResponse } from '@/types/api';
import type {
  LoginRequest,
  BrowserSessionResponse,
  User,
  ChangePasswordPayload,
  UpdateProfilePayload,
} from '../types/auth';

interface ChangePasswordResponse {
  user_id: number;
  username: string;
  message: string;
}

/**
 * Login with username and password
 */
export const login = async (credentials: LoginRequest): Promise<BrowserSessionResponse> => {
  return withBrowserSessionLock(async () => {
    const response = await browserSessionClient.post<
      BrowserSessionResponse | ApiResponse<BrowserSessionResponse>
    >(
      '/api/v1/auth/browser/login',
      credentials,
    );
    const session = unwrapBrowserSession(response.data);
    clearCachedSessionData();
    useAuthStore.getState().setAuth(session.access_token, session.user);
    announceBrowserSessionMutation('changed');
    return session;
  });
};

export interface ReplaceTemporaryPasswordRequest {
  username: string;
  temporary_password: string;
  new_password: string;
}

/**
 * Replace an admin-issued temporary password with the person's own password.
 * No session is issued: sign in with the new password afterwards. Sent with the
 * browser-session client because the person is not signed in yet.
 */
export const replaceTemporaryPassword = async (
  payload: ReplaceTemporaryPasswordRequest,
): Promise<{ message: string }> => {
  const response = await browserSessionClient.post<ApiResponse<{ message: string }>>(
    '/api/v1/auth/temporary-password',
    payload,
  );
  return response.data.data;
};

/**
 * Logout (clear server-side session if needed)
 */
export const logout = async (): Promise<void> => {
  await withBrowserSessionLock(async () => {
    await browserSessionClient.post('/api/v1/auth/browser/logout');
    useAuthStore.getState().clearAuth();
    clearCachedSessionData();
    announceBrowserSessionMutation('ended');
  });
};

/**
 * Get current user information
 */
export const getCurrentUser = async (): Promise<User> => {
  const response = await apiClient.get<User>('/api/v1/auth/me');
  return response.data as User;
};

/**
 * Change current user's password
 */
export const changePassword = async (
  payload: ChangePasswordPayload,
): Promise<ChangePasswordResponse> => {
  const response = await apiClient.post<ChangePasswordResponse>('/api/v1/auth/change-password', payload);
  return response.data;
};

/**
 * Update current user's profile/contact information
 */
export const updateProfile = async (payload: UpdateProfilePayload): Promise<User> => {
  const response = await apiClient.put<User>('/api/v1/auth/profile', payload);
  return response.data;
};
